import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import clsx from 'clsx';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';

import type { IAIGeneratingModalSessionData, TAIGeneratingCardStatus } from '../../interfaces/IAIGeneratingModal';
import type IAIGeneratingModalSessionsList from './interfaces/IAIGeneratingModalSessionsList';

import styles from './sass/AIGeneratingModalSessionsList.module.scss';

function AIGeneratingModalSessionsList({
  listClassName,
  sessions,
  onSessionOpen,
  getSessionStatus,
  getSessionDisplayName,
  layout = 'list',
  activeSessionId,
  onActiveSessionChange,
}: IAIGeneratingModalSessionsList) {
  const { t } = useTranslation();
  const isCarousel = layout === 'carousel';
  const carouselIndex = Math.max(
    0,
    sessions.findIndex(({ session: sessionItem }) => sessionItem.id === activeSessionId),
  );

  const getStatusLabel = (status: TAIGeneratingCardStatus) => {
    if (status === VALUE_CHAIN_AI_AGENT.STATUS_DONE) {
      return t('valueChainMap.aiGeneratingStatusDone');
    }

    if (status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS) {
      return t('valueChainMap.aiGeneratingStatusInProgress');
    }

    return t('valueChainMap.aiGeneratingStatusEmpty');
  };

  useEffect(() => {
    if (!isCarousel || !sessions.length) {
      return;
    }

    const hasActiveSession = sessions.some(({ session: sessionItem }) => sessionItem.id === activeSessionId);
    const fallbackSessionId = sessions[0]?.session.id;

    if (!hasActiveSession && fallbackSessionId) {
      onActiveSessionChange?.(fallbackSessionId);
    }
  }, [activeSessionId, isCarousel, onActiveSessionChange, sessions]);

  const renderSessionButton = (
    sessionData: IAIGeneratingModalSessionData,
    isCarouselCard = false,
  ) => {
    const { session: sessionItem } = sessionData;
    const sessionStatus = getSessionStatus(sessionData);
    const sessionDisplayName = getSessionDisplayName(sessionData);
    const sessionStatusLabel = getStatusLabel(sessionStatus);

    return (
      <button
        key={sessionItem.id}
        type="button"
        className={clsx(styles.sessionButton, {
          [styles.carouselCard]: isCarouselCard,
        })}
        onClick={() => onSessionOpen(sessionItem.id)}
        aria-label={`${sessionDisplayName}. Session ${sessionItem.id}. Status: ${sessionStatusLabel}`}
      >
        <span
          className={clsx(
            styles.sessionStatusIndicator,
            styles[`sessionStatus_${sessionStatus}`],
          )}
          aria-hidden="true"
        >
          {sessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_DONE ? (
            <span className={clsx(styles.tabStatusIndicator, styles.tabStatusDone)} aria-hidden="true">
              <CheckIcon />
            </span>
          ) : null}
          {sessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS ? (
            <span className={styles.spinner} />
          ) : null}
        </span>
        <span className={styles.sessionContent}>
          <span className={styles.sessionTitle}>{sessionDisplayName}</span>
          <span className={styles.sessionDescription}>{`Status: ${sessionStatusLabel}`}</span>
        </span>
        <ArrowRightIcon className={styles.sessionArrow} aria-hidden="true" />
      </button>
    );
  };

  const goToCarouselIndex = (nextIndex: number) => {
    const clampedIndex = Math.min(Math.max(nextIndex, 0), sessions.length - 1);
    const nextSessionId = sessions[clampedIndex]?.session.id;

    if (nextSessionId) {
      onActiveSessionChange?.(nextSessionId);
    }
  };

  if (!isCarousel) {
    return (
      <div className={listClassName} role="list">
        {sessions.map((sessionData) => renderSessionButton(sessionData))}
      </div>
    );
  }

  const activeSessionData = sessions[carouselIndex] || sessions[0];

  if (!activeSessionData) {
    return null;
  }

  return (
    <div
      className={clsx(listClassName, styles.carousel)}
      aria-roledescription="carousel"
    >
      <button
        type="button"
        className={styles.carouselNavButton}
        onClick={() => goToCarouselIndex(carouselIndex - 1)}
        disabled={carouselIndex <= 0}
        aria-label={t('common.back')}
      >
        <ArrowLeftIcon aria-hidden="true" />
      </button>
      <div className={styles.carouselTrack} role="list">
        {renderSessionButton(activeSessionData, true)}
      </div>
      <button
        type="button"
        className={styles.carouselNavButton}
        onClick={() => goToCarouselIndex(carouselIndex + 1)}
        disabled={carouselIndex >= sessions.length - 1}
        aria-label={t('common.next')}
      >
        <ArrowRightIcon aria-hidden="true" />
      </button>
    </div>
  );
}

export default AIGeneratingModalSessionsList;
